import { useState, useRef, useEffect } from 'react';
import { Button } from '@radix-ui/themes';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import PropTypes from 'prop-types';

/**
 * Banner component for issue actions (submit, validate, reject)
 * @param {string} props.title - Title text displayed on the left
 * @param {React.ReactNode} props.icon - Optional icon displayed before the title
 * @param {string} props.buttonText - Text displayed on the button (single button mode)
 * @param {React.ReactNode} props.buttonIcon - Optional icon displayed in the button (single button mode)
 * @param {Function} props.onClick - Function called when button is clicked (single button mode)
 * @param {boolean} props.disabled - Whether the button is disabled (single button mode)
 * @param {string} props.buttonClassName - Additional CSS classes for the button (single button mode)
 * @param {Array} props.buttons - Array of button configurations for multiple buttons mode
 * @param {boolean} props.isInWebView - Whether the app is in a web view (affects positioning)
 */
const IssueActionBanner = ({
  title,
  icon,
  buttonText,
  buttonIcon,
  onClick,
  disabled = false,
  buttonClassName = '',
  buttons = null,
  isInWebView = false
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const primaryButton = buttons?.find(b => b.variant === 'primary');
  const secondaryButtons = buttons?.filter(b => b.variant !== 'primary') || [];

  return (
    <div className={`${!isInWebView ? 'fixed' : 'sticky'} ${!isInWebView ? 'bottom-[75px]' : 'bottom-0'} z-40 w-full left-0 right-0 border-t-2 border-gray-200 bg-white/95 backdrop-blur-sm supports-[backdrop-filter]:bg-white/80 shadow-lg`}>
      <div className="container mx-auto py-3 px-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            {icon && icon}
            {title && <span className="font-medium">{title}</span>}
          </div>
          <div className="flex items-center gap-2">
            {buttons && primaryButton ? (
              <Button
                size="3"
                disabled={primaryButton.disabled}
                onClick={primaryButton.onClick}
                className={`!font-semibold !gap-1.5 ${
                  primaryButton.disabled
                    ? '!bg-gray-300 !text-gray-500 !cursor-not-allowed'
                    : '!bg-black !text-white hover:!bg-gray-800'
                } ${primaryButton.className || ''}`}
              >
                {primaryButton.icon && primaryButton.icon}
                {primaryButton.text}
              </Button>
            ) : null}
            {buttons && secondaryButtons.length > 0 && (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                >
                  <DotsHorizontalIcon className="w-5 h-5" />
                </button>
                {isMenuOpen && (
                  <div className="absolute bottom-full right-0 mb-2 min-w-[220px] rounded-md border border-gray-200 bg-white shadow-lg z-50 py-1">
                    {secondaryButtons.map((button, index) => (
                      <button
                        key={index}
                        type="button"
                        disabled={button.disabled}
                        onClick={() => {
                          setIsMenuOpen(false);
                          button.onClick();
                        }}
                        className={`flex w-full items-center justify-start gap-2 px-4 py-2.5 text-sm text-left transition-colors ${
                          button.disabled
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <span>{button.text}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!buttons && (
              <Button
                size="3"
                disabled={disabled}
                onClick={onClick}
                className={`!font-semibold ${
                  disabled
                    ? '!bg-gray-300 !text-gray-500 !cursor-not-allowed'
                    : '!bg-black !text-white hover:!bg-gray-800'
                } ${buttonClassName}`}
              >
                {buttonIcon && buttonIcon}
                {buttonText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

IssueActionBanner.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  buttonText: PropTypes.string,
  buttonIcon: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  buttonClassName: PropTypes.string,
  buttons: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
    icon: PropTypes.node,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    variant: PropTypes.oneOf(['primary', 'secondary']),
    className: PropTypes.string
  })),
  isInWebView: PropTypes.bool
};

export { IssueActionBanner };
